import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ContextRequest, ContextResponse } from './models/context-takeover.api.model';
import { TopicAgentRequest, TopicSearchResponse } from './models/topic-agent.api.model';
import { HttpVerbsEnum } from '../shared/enums/http-verbs.enum';
import { NeptuneApi } from '../genericApi/neptune.api';
import { SavedMentionSearchApiModel } from '../mentions/model/saved-mention-search.api.model';
import { PrecisionClipApi, PrecisionClipResponse } from './models/precision-clip.api.model';
import { AgentConfigurationApi } from './models/agent-configuration.api.model';

// To use the local versions if you're running the agent locally
const CONTEXT_AGENT_URL = 'http://localhost:3000/api/v1/agents/context';
const TOPIC_AGENT_URL = 'http://localhost:3000/api/v1/agents/topic-search';

@Injectable()
export class AgentsApiService {

    constructor(
        private neptuneApi: NeptuneApi
    ) {}

    getContext(
        currentPrompt: string,
        taskDescription: string,
        conversationHistory: ContextRequest['conversationHistory'] = [],
        systemPrompt: string | null = null
    ): Observable<ContextResponse> {
        const slug: string = '/agents/context';

        const body: ContextRequest = {
            taskDescription: taskDescription,
            currentPrompt,
            ...(conversationHistory.length ? { conversationHistory } : {}),
            ...(systemPrompt ? { systemPrompt } : {})
        };
        return this.neptuneApi.api<ContextResponse>(slug, body, HttpVerbsEnum.POST);
    }

    createTopic(userMessage: string, systemPrompt: string | null = null): Observable<TopicSearchResponse> {
        const slug: string = '/agents/topic-search';

        const body: TopicAgentRequest = {
            userMessage,
            ...(systemPrompt ? { systemPrompt } : {})
        };
        return this.neptuneApi.api<TopicSearchResponse>(slug, body, HttpVerbsEnum.POST);
    }

    updateTopic(topic: SavedMentionSearchApiModel, userMessage: string): Observable<TopicSearchResponse> {
        const slug: string = '/agents/topic-search/update';

        return this.neptuneApi.api<TopicSearchResponse>(
            slug,
            { topic, userMessage },
            HttpVerbsEnum.POST
        );
    }

    getPrecisionClip(precisionClip: PrecisionClipApi): Observable<PrecisionClipResponse> {
        const slug: string = '/agents/auto-clipper';

        return this.neptuneApi.api<PrecisionClipResponse>(
            slug,
            precisionClip,
            HttpVerbsEnum.POST
        );
    }

    getMonitorQuestionLimit(): Observable<AgentConfigurationApi> {
        const slug: string = '/agents/agent-configuration/monitor-question-limit';

        return this.neptuneApi.api<AgentConfigurationApi>(slug, null, HttpVerbsEnum.GET);
    }

    getAgentConfiguration(accountId: number): Observable<AgentConfigurationApi> {
        const slug: string = `/agents/agent-configuration/${accountId}`;

        return this.neptuneApi.api<AgentConfigurationApi>(slug, null, HttpVerbsEnum.GET);
    }

    createAgentConfiguration(request: AgentConfigurationApi): Observable<AgentConfigurationApi> {
        const slug: string = '/agents/agent-configuration';

        return this.neptuneApi.api<AgentConfigurationApi>(slug, request, HttpVerbsEnum.POST);
    }

    updateAgentConfiguration(accountId: number, request: AgentConfigurationApi): Observable<AgentConfigurationApi> {
        const slug: string = `/agents/agent-configuration/${accountId}`;

        return this.neptuneApi.api<AgentConfigurationApi>(slug, request, HttpVerbsEnum.PATCH);
    }

    deleteAgentConfiguration(accountId: number): Observable<void> {
        const slug: string = `/agents/agent-configuration/${accountId}`;

        return this.neptuneApi.api<void>(slug, null, HttpVerbsEnum.DELETE);
    }
}
